










































import { computed, defineComponent, ref } from '@vue/composition-api';
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue';
import soilApiService from '../../services/soilApiService';
import { useAllResources } from '../../store/modules/resources';
import { isFulfilled, isRejected } from '../../utils/typeHelpers';

export default defineComponent({
	components: { ConfirmDialog },
	inheritAttrs: false,
	props: {
		fieldIds: {
			type: Array as () => string[],
			required: true,
		},
	},
	emits: ['deleteSuccess'],
	setup(props, { root, emit }) {
		const showConfirmationDialog = ref<boolean>(false);
		const errorMessages = ref<string[] | null>(null);
		const isLoading = ref<boolean>(false);

		const { isLoaded } = useAllResources(root.$store);

		const selectedFields = computed(() =>
			isLoaded.value ? props.fieldIds.map((fieldId) => root.$store.getters['resources/getFieldById'](fieldId)) : []
		);
		const title = computed(() =>
			isLoaded && selectedFields.value.length === 1
				? `Delete ${selectedFields.value[0].name}`
				: `Delete ${selectedFields.value.length} Fields`
		);
		const buttonText = computed(() =>
			props.fieldIds.length <= 1 ? 'Delete' : `Delete (${selectedFields.value.length})`
		);
		const warningText = computed(
			() =>
				`You will no longer be able to recover ${
					props.fieldIds.length === 1 ? 'this field' : 'these fields'
				} once you delete. Are you sure you want to delete?`
		);

		const cleanup = () => {
			isLoading.value = false;
			errorMessages.value = null;
		};

		const handleConfirm = async () => {
			if (errorMessages.value) {
				showConfirmationDialog.value = false;
				return;
			}

			isLoading.value = true;
			const responses = await Promise.allSettled<{
				fieldId: string;
				response: Awaited<ReturnType<typeof soilApiService.deleteField>>;
			}>(
				props.fieldIds.map(
					(fieldId) =>
						// eslint-disable-next-line no-async-promise-executor
						new Promise(async (resolve, reject) => {
							try {
								resolve({
									fieldId,
									response: await soilApiService.deleteField(fieldId),
								});
							} catch (error) {
								reject({
									fieldId,
									response: error,
								});
							}
						})
				)
			);
			isLoading.value = false;

			const successes = responses.filter(isFulfilled).map((success) => success.value);
			const failures = responses.filter(isRejected).map((failure) => failure.reason);

			if (successes.length) {
				emit(
					'deleteSuccess',
					successes.map(({ fieldId }) => fieldId)
				);
			}

			if (successes.length === responses.length) {
				errorMessages.value = null;
				showConfirmationDialog.value = false;
			} else {
				errorMessages.value = failures.map(({ fieldId, response }) => {
					const field = selectedFields.value.find(({ id }) => id === fieldId);
					return `${field?.name}: ${response?.response?.data?.message}`;
				});
			}

			if (failures.length !== responses.length) {
				root.$store.dispatch('resources/getAllResources');
			}
		};

		return {
			showConfirmationDialog,
			title,
			buttonText,
			warningText,
			isLoading,
			handleConfirm,
			errorMessages,
			cleanup,
		};
	},
});
